// src/api.ts

// API service using fetch (no axios)
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://127.0.0.1:5000/api';

// Helper function to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('cardbox_token') : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Generic fetch wrapper
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

// ✅ 1. Add this Interface
export interface LoginResponse {
  message: string;
  user: any;
  token?: string;              // Standard Login Token
  require_grid_auth?: boolean; // New Flag for Super Admin
  temp_token?: string;         // New Temp Token for Grid Auth
}

// Auth API
export const authApi = {
  register: (data: { phone_number: string; password: string; full_name: string }) =>
    request<{ message: string; token: string; user: any }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

 login: (data: { phone_number: string; password: string }) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  logout: () =>
    request<{ message: string }>('/auth/logout', {
      method: 'POST',
    }),

  // 1. Request OTP
  forgotPassword: (phone_number: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ phone_number }),
    }),

  // 2. Verify OTP
  verifyOtp: (phone_number: string, otp: string) =>
    request<{ message: string }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phone_number, otp }),
    }),

  // 3. Reset Password
  resetPassword: (data: { phone_number: string; otp: string; new_password: string }) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Profile API
export const profileApi = {

  detectLocation: (lat: number, lon: number) =>
    request<any>(`/my-profile/geocode?lat=${lat}&lon=${lon}`),

  getProfile: () =>
    request<any>('/my-profile'),

  getDetails: () =>
    request<any>('/my-profile/details'),

  // ✅ UPDATED: Supports both JSON (Text) and FormData (Files like QR)
  updateDetails: (payload: any) => {
    // 1. Check if payload is FormData (File Upload)
    if (payload instanceof FormData) {
      const token = sessionStorage.getItem('cardbox_token');
      const headers: HeadersInit = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      return fetch(`${API_BASE_URL}/my-profile/details`, {
        method: 'PUT',
        headers, // ⚠️ Let browser set Content-Type for FormData
        body: payload,
      }).then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to update details');
        return data;
      });
    }

    // 2. Default JSON behavior (Text Updates)
    return request<{ message: string; provider: any }>('/my-profile/details', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    
  },

  uploadGalleryImage: (formData: FormData) => {
    const token = sessionStorage.getItem('cardbox_token');
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return fetch(`${API_BASE_URL}/my-profile/gallery`, {
      method: 'POST',
      headers,
      body: formData,
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload image');
      return data;
    });
  },

  deleteGalleryImage: (id: number) =>
    request<{ message: string }>(`/my-profile/gallery/${id}`, {
      method: 'DELETE',
    }),

    deleteLogo: () =>
    request<{ message: string; provider: any }>('/my-profile/logo', {
      method: 'DELETE',
    }),

  deleteCover: () =>
    request<{ message: string; provider: any }>('/my-profile/cover', {
      method: 'DELETE',
    }),

  createProvider: (data: {
    business_name: string;
    service_category: string;
    description?: string;
    contact_phone: string;
    whatsapp_number: string;
    experience?: string;
    email?: string;
    city: string;
  }) =>
    request<{ message: string; provider: any }>('/my-profile/create-provider', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ✅ UPDATED: Supports both JSON (Text) and FormData (Files like Logo/Cover)
  updateProfile: (data: any) => {
    // 1. Check if data is FormData (File Upload)
    if (data instanceof FormData) {
      const token = sessionStorage.getItem('cardbox_token');
      const headers: HeadersInit = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      return fetch(`${API_BASE_URL}/my-profile/update`, {
        method: 'PUT',
        headers, // ⚠️ Let browser set Content-Type for FormData
        body: data,
      }).then(async (res) => {
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Failed to update profile');
        return result;
      });
    }

    // 2. Default JSON behavior (Text Updates)
    return request<{ message: string; provider: any }>('/my-profile/update', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  uploadProfileImage: (formData: FormData) => {
    const token = sessionStorage.getItem('cardbox_token');
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return fetch(`${API_BASE_URL}/my-profile/upload-image`, {
      method: 'POST',
      headers,
      body: formData,
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    });
  },

  deleteProfileImage: () =>
    request<{ message: string; provider: any }>('/my-profile/profile-image', {
      method: 'DELETE',
    }),

  toggleAvailability: (status: 'Available' | 'Busy' | 'Disabled') =>
    request<{ message: string; availability_status: string }>('/my-profile/availability', {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  getAnalytics: () =>
    request<{
      view_count: number;
      phone_click_count: number;
      whatsapp_click_count: number;
      total_ratings: number;
      rating_breakdown?: {
        p: number;
        'p+': number;
        'p++': number;
        'p*': number;
      };
      // ✅ ADD THIS
      saved_by_users: Array<{
        user_id: number;
        full_name: string;
        phone_number: string;
        saved_at: string;
      }>;
      endorsed_by_users?: Array<{
        user_id: number;
        full_name: string;
        phone_number: string;
        endorsed_at: string;
      }>;
    }>('/my-profile/analytics'),
};




// 1. Add this new interface for the pagination details
export interface PaginationMeta {
  total_items: number;
  total_pages: number;
  current_page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}
// Provider API
// 1. Define the response type (or import it if defined elsewhere)
interface SearchResponse {
  total: number;
  providers: any[]; // Better: use Provider[] if imported
  ai_category?: string; // ✅ Added optional field
  ai_location?: string; // ✅ Added optional field
  pagination?: PaginationMeta; // ✅ ADD THIS FIELD
}

export const providerApi = {
  search: (params: any) => {
    const queryString = new URLSearchParams(params).toString();
    // 2. Update the generic type here 👇
    return request<SearchResponse>(
      `/providers${queryString ? `?${queryString}` : ''}`
    );
  },

  // ... rest of your API remains exactly the same ...
  getCategories: () => request<string[]>('/providers/categories'),

  getById: (id: number) =>
    request<any>(`/providers/${id}`),

  trackView: (id: number) =>
    request<{ message: string; view_count: number }>(`/providers/${id}/view`, {
      method: 'POST',
    }),

  trackPhoneClick: (id: number) =>
    request<{ message: string; phone_click_count: number }>(`/providers/${id}/phone-click`, {
      method: 'POST',
    }),

  trackWhatsappClick: (id: number) =>
    request<{ message: string; whatsapp_click_count: number }>(`/providers/${id}/whatsapp-click`, {
      method: 'POST',
    }),
};
// Rating API
export const ratingApi = {
  create: (data: {
    provider_id: number;
    rating_value: 'p' | 'p+' | 'p++' | 'p*';
    review_text?: string;
  }) =>
    request<{ message: string; rating: any }>('/ratings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request<{ message: string }>(`/ratings/${id}`, {
      method: 'DELETE',
    }),
};


// ✅ NEW: Wishlist API
export const wishlistApi = {
  // Get all wishlisted cards
  getWishlist: () =>
    request<{ total: number; wishlist: any[] }>('/wishlist'),

  // Add a specific provider to wishlist
  add: (providerId: number) =>
    request<{ message: string }>(`/wishlist/${providerId}`, {
      method: 'POST',
    }),

  // Remove a specific provider from wishlist
  remove: (providerId: number) =>
    request<{ message: string }>(`/wishlist/${providerId}`, {
      method: 'DELETE',
    }),

  // Check if a provider is already wishlisted (for the red/white heart status)
  checkStatus: (providerId: number) =>
    request<{ is_wishlisted: boolean }>(`/wishlist/check/${providerId}`),
};

// ✅ NEW: Subscription API
export const subscriptionApi = {
  // Step 1: Create Order
  createOrder: (data?: { plan_type: string }) =>
    request<{
      order_id: string;
      amount: number;
      currency: string;
      key: string; // Backend now sends the Razorpay Key ID
      user_details: { name: string; email: string; contact: string };
    }>('/providers/subscribe', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Step 2: Verify Payment
  verifyPayment: (paymentData: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
    plan_type?: string;
  }) =>
    request<{ message: string; new_expiry: string }>('/providers/verify-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    }),
};

// ✅ NEW: Endorsement (Trust) API
export const endorsementApi = {
  // Endorse a provider
  endorse: (providerId: number) =>
    request<{ message: string; trust_count: number }>(`/endorse/${providerId}`, {
      method: 'POST',
    }),

  // Remove endorsement
  remove: (providerId: number) =>
    request<{ message: string; trust_count: number }>(`/endorse/${providerId}`, {
      method: 'DELETE',
    }),

  // Check if current user has endorsed
  checkStatus: (providerId: number) =>
    request<{ is_endorsed: boolean }>(`/endorse/check/${providerId}`),
};
// ✅ NEW: Product / Catalog API
export const productApi = {
  // Get all products for the logged-in provider
  getAll: () =>
    request<{ count: number; products: any[] }>('/products'),

  // Add a product (Supports Image)
  create: (formData: FormData) => {
    const token = sessionStorage.getItem('cardbox_token');
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers,
      body: formData,
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add product');
      return data;
    });
  },

  // Update a product (Supports Image)
  update: (id: number, formData: FormData) => {
    const token = sessionStorage.getItem('cardbox_token');
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers,
      body: formData,
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update product');
      return data;
    });
  },

  // Delete a product
  delete: (id: number) =>
    request<{ message: string }>(`/products/${id}`, {
      method: 'DELETE',
    }),
};
// Admin API
// Admin API
export const adminApi = {
  getUsers: () =>
    request<{ total: number; users: any[] }>('/admin/users'),

  getProviders: () =>
    request<{ total: number; providers: any[] }>('/admin/providers'),

  getRatings: () =>
    request<{ total: number; ratings: any[] }>('/admin/ratings'),

  toggleProvider: (id: number) =>
    request<{ message: string; is_active: boolean }>(`/admin/providers/${id}/toggle`, {
      method: 'PUT',
    }),

  toggleRating: (id: number) =>
    request<{ message: string; is_active: boolean }>(`/admin/ratings/${id}/toggle`, {
      method: 'PUT',
    }),

  toggleUser: (id: number) =>
    request<{ message: string; is_active: boolean }>(`/admin/users/${id}/toggle`, {
      method: 'PUT',
    }),

  // ✅ NEW: Super Admin - Assign Role (Promote to Admin / Demote to User)
  assignRole: (userId: number, role: string) =>
    request<{ message: string }>('/admin/assign-role', {
      method: 'PUT',
      body: JSON.stringify({ user_id: userId, role }),
    }),

  // ✅ NEW: Super Admin - Hard Delete Provider
  deleteProvider: (id: number) =>
    request<{ message: string }>(`/admin/providers/${id}/delete`, {
      method: 'DELETE',
    }),

  // Inside adminApi object...

  // ✅ NEW: Get List of Tables
  getDatabaseTables: () =>
    request<{ tables: string[] }>('/admin/database/tables'),

  // ✅ NEW: Get Data for a Table
  getTableData: (tableName: string) =>
    request<{ columns: string[]; rows: any[] }>(`/admin/database/table/${tableName}`),

  manualSubscription: (providerId: number) =>
    request<{ message: string; provider: any }>(`/admin/providers/${providerId}/manual-subscription`, {
      method: 'PUT',
    }),

  cancelSubscription: (providerId: number) =>
    request<{ message: string; provider: any }>(`/admin/providers/${providerId}/cancel-subscription`, {
      method: 'PUT',
    }),

    // ✅ NEW: Super Admin - User Impersonation
  impersonateUser: (userId: number) => 
  request<{ token: string; user: any; message: string }>(`/admin/impersonate/${userId}`, {
    method: "POST",
  }),

  // ...
};